import { fetchWithAuth } from '../lib/fetchWithAuth';

const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

type PresignResponse = {
  uploadId: string;
  uploadUrl: string;
  headers?: Record<string, string>;
  fields?: Record<string, string>;
  method?: string;
};

export type UploadedFileMeta = {
  fileUrl: string;
  fileName: string;
  uploadedAt: string;
  size: number;
  mimeType: string;
  uploadId?: string;
  publicUrl?: string;
  scanStatus?: 'pending' | 'clean' | 'blocked';
  scanCompletedAt?: string;
};

type PresignRequestBody = {
  kind: 'candidate-cv' | 'message-attachment' | 'logo' | 'company-cover' | 'company-gallery';
  filename: string;
  contentType: string;
  size: number;
};

async function presignUpload(body: PresignRequestBody): Promise<PresignResponse> {
  if (!API_BASE_URL) {
    throw new Error('Presign not available without API');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/uploads/presign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Upload presign failed (${response.status}): ${errorText}`);
  }
  const payload = (await response.json()) as PresignResponse;
  if (!payload?.uploadId || !payload.uploadUrl) {
    throw new Error('Upload presign response malformed');
  }
  return payload;
}

type UploadCompletionResponse = {
  fileUrl?: string;
  publicUrl?: string;
  scanStatus?: 'pending' | 'clean' | 'blocked';
  scanCompletedAt?: string;
};

async function completeUpload(uploadId: string, metadata: Record<string, unknown>): Promise<UploadCompletionResponse> {
  if (!API_BASE_URL) {
    return { fileUrl: '' };
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/uploads/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ uploadId, metadata }),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Upload completion failed (${response.status}): ${errorText}`);
  }
  return (await response.json()) as UploadCompletionResponse;
}

async function uploadToTarget(file: File, presign: PresignResponse) {
  if (presign.fields) {
    const formData = new FormData();
    Object.entries(presign.fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append('file', file);
    const response = await fetch(presign.uploadUrl, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      throw new Error(`Upload failed (${response.status})`);
    }
    return;
  }
  const targetHeaders = {
    'Content-Type': file.type || 'application/octet-stream',
    ...(presign.headers ?? {}),
  };
  const useAuthForUpload =
    presign.uploadUrl.startsWith('/') ||
    (API_BASE_URL && presign.uploadUrl.startsWith(API_BASE_URL));
  const response = await (useAuthForUpload ? fetchWithAuth : fetch)(presign.uploadUrl, {
    method: presign.method ?? 'PUT',
    headers: targetHeaders,
    body: file,
  });
  if (!response.ok) {
    throw new Error(`Upload failed (${response.status})`);
  }
}

const fileToDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export type UploadStatusResponse = {
  uploadId: string;
  scanStatus: 'pending' | 'clean' | 'blocked';
  scanCompletedAt?: string;
  fileUrl?: string;
  publicUrl?: string;
  fileName?: string;
  size?: number;
  mimeType?: string;
};

export async function fetchUploadStatus(uploadId: string): Promise<UploadStatusResponse> {
  if (!API_BASE_URL) {
    throw new Error('Upload status unavailable without API access');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/uploads/status/${uploadId}`);
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Unable to fetch upload status (${response.status}): ${errorText}`);
  }
  return (await response.json()) as UploadStatusResponse;
}

async function waitForScan(uploadId: string, attempts = 6, delayMs = 2000) {
  for (let i = 0; i < attempts; i += 1) {
    try {
      const status = await fetchUploadStatus(uploadId);
      if (status.scanStatus !== 'pending') {
        return status;
      }
    } catch {
      // ignore and retry
    }
    if (i < attempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  return undefined;
}

async function uploadFile(
  kind: 'candidate-cv' | 'message-attachment' | 'logo' | 'company-cover' | 'company-gallery',
  file: File,
): Promise<UploadedFileMeta> {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error('Files must be 10MB or smaller.');
  }
  const uploadedAt = new Date().toISOString();
  if (!API_BASE_URL) {
    const dataUrl = await fileToDataUrl(file);
    if (!dataUrl) {
      throw new Error('Unable to read file');
    }
    return {
      fileUrl: dataUrl,
      fileName: file.name,
      uploadedAt,
      size: file.size,
      mimeType: file.type || 'application/octet-stream',
    };
  }

  let presign: PresignResponse;
  try {
    presign = await presignUpload({
      kind,
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      size: file.size,
    });
  } catch (error) {
    const message =
      error instanceof Error ? error.message : typeof error === 'string' ? error : 'Upload presign failed';
    throw new Error(message);
  }

  await uploadToTarget(file, presign);

  const completion = await completeUpload(presign.uploadId, {
    kind,
    filename: file.name,
  });

  let fileUrl = completion.fileUrl ?? completion.publicUrl;
  let publicUrl = completion.publicUrl;
  let scanStatus = completion.scanStatus ?? 'pending';
  let scanCompletedAt = completion.scanCompletedAt;

  if (presign.uploadId && scanStatus !== 'clean') {
    const refreshed = await waitForScan(presign.uploadId);
    if (refreshed) {
      scanStatus = refreshed.scanStatus;
      fileUrl = refreshed.fileUrl ?? refreshed.publicUrl ?? fileUrl;
      publicUrl = refreshed.publicUrl ?? publicUrl;
      scanCompletedAt = refreshed.scanCompletedAt ?? scanCompletedAt;
    }
  }

  if (!fileUrl) {
    throw new Error('Upload completion response missing file URL');
  }

  return {
    fileUrl,
    fileName: file.name,
    uploadedAt,
    size: file.size,
    mimeType: file.type || 'application/octet-stream',
    uploadId: presign.uploadId,
    publicUrl,
    scanStatus,
    scanCompletedAt,
  };
}

export async function uploadCandidateCv(file: File): Promise<UploadedFileMeta> {
  return uploadFile('candidate-cv', file);
}

export async function uploadMessageAttachment(file: File): Promise<UploadedFileMeta> {
  return uploadFile('message-attachment', file);
}

export async function uploadCompanyAsset(
  kind: 'logo' | 'company-cover' | 'company-gallery',
  file: File,
): Promise<UploadedFileMeta> {
  return uploadFile(kind, file);
}

export async function deleteUpload(uploadId: string): Promise<boolean> {
  if (!uploadId) return false;
  if (!API_BASE_URL) return true;
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/uploads/${uploadId}`, { method: 'DELETE' });
  if (response.status === 404) return true;
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'request failed');
    throw new Error(`Failed to delete upload (${response.status}): ${errorText}`);
  }
  return true;
}
